import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import PropTypes from 'prop-types';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/ApiConfig';
import LoadingModal from '../modals/LoadingModal';
import Pagination10 from '../Pagination10';
import PageFooter from '../PageFooter';
import TableHeader from '../TableHeader';

const ClienteBuscaModal = ({ isOpen, onClose, onSelect }) => {
  const [clientes, setClientes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filtro, setFiltro] = useState('');
  const [selectedCliente, setSelectedCliente] = useState(null);

  // Estados da paginação
  const [startIndex, setStartIndex] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const token = localStorage.getItem('token');

  // Normalizar string para busca
  const normalizeString = str => {
    return str
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toUpperCase();
  };

  // Filtrar clientes
  const clientesFiltrados = clientes.filter(c => {
    const normalizedBusca = normalizeString(filtro);
    return (
      normalizeString(c.nome).includes(normalizedBusca) ||
      normalizeString(c.cnpj).includes(normalizedBusca) ||
      normalizeString(c.logrCidade).includes(normalizedBusca)
    );
  });

  // Aplicar paginação aos clientes filtrados
  const clientesPaginados = clientesFiltrados.slice(
    startIndex,
    startIndex + rowsPerPage
  );

  // Funções de controle da paginação
  const handleRowsPerPageChange = event => {
    const newRowsPerPage = parseInt(event.target.value);
    setRowsPerPage(newRowsPerPage);
    setStartIndex(0); // Voltar para primeira página
  };

  const handleFirstItems = () => setStartIndex(0);

  const handlePreviousItems = () => {
    const newStart = Math.max(0, startIndex - rowsPerPage);
    setStartIndex(newStart);
  };

  const handleNextItems = () => {
    const newStart = Math.min(
      clientesFiltrados.length - rowsPerPage,
      startIndex + rowsPerPage
    );
    setStartIndex(newStart);
  };

  const handleLastItems = () => {
    const lastStart =
      Math.floor((clientesFiltrados.length - 1) / rowsPerPage) * rowsPerPage;
    setStartIndex(Math.max(0, lastStart));
  };

  // Reset pagination when filter changes
  useEffect(() => {
    setStartIndex(0);
  }, [filtro]);

  // Carregar clientes ao abrir modal
  useEffect(() => {
    if (isOpen) {
      setIsLoading(true);
      axios
        .get(`${API_BASE_URL.CLIENTE}/all`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then(result => {
          setClientes(result.data);
        })
        .catch(error => {
          console.error('Erro ao carregar clientes:', error);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      // Limpar ao fechar
      setFiltro('');
      setSelectedCliente(null);
      setStartIndex(0);
    }
  }, [isOpen, token]);

  const handleSelect = () => {
    if (selectedCliente && onSelect) {
      onSelect(selectedCliente);
    }
    onClose();
  };

  const handleDoubleClick = cliente => {
    if (onSelect) {
      onSelect(cliente);
    }
    onClose();
  };

  return (
    <>
      <Modal show={isOpen} onHide={onClose} size="lg" backdrop="static">
        <div className="modal-header text-white py-1" style={{ background: 'linear-gradient(90deg, #0b3383, #0e48ad)' }}>
            <h6 className="modal-title text-center m-0">
              <i className="bi bi-search me-2"></i>
              Buscar Entidade
            </h6>
            <button
              className="btn btn-link text-white p-0 mb-0"
              onClick={onClose}
              title="Fechar"
              style={{ fontSize: '0.9rem', textDecoration: 'none' }}
            >
              <i className="bi bi-x-lg"></i>
            </button>
          </div>

        <Modal.Body style={{ maxHeight: '500px', overflowY: 'auto' }}>
          {/* Filtro de busca */}
          <Row className="mb-3">
            <Col>
              <div className="d-flex align-items-center gap-2">
                <div className="input-group input-group-sm" style={{ width: '320px' }}>
                  <span className="input-group-text small text-muted" aria-hidden="true">
                    <i className="bi bi-funnel" aria-hidden="true" />
                    <span className="visually-hidden">Buscar</span>
                  </span>
                  <Form.Control
                    type="text"
                    className="filter-input-compact"
                    placeholder="CNPJ, Nome ou Cidade..."
                    value={filtro}
                    onChange={e => setFiltro(e.target.value.toUpperCase())}
                    size="sm"
                  />
                </div>
                {filtro && (
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => setFiltro('')}
                    title="Limpar filtro"
                    className="ms-2"
                  >
                    <i className="bi bi-x-circle"></i>
                  </Button>
                )}
              </div>
            </Col>
          </Row>

          {/* Tabela de clientes */}
          <div
            className="table-responsive small"
            style={{ maxHeight: '300px', overflowY: 'auto' }}
          >
            <table className="table table-striped table-hover table-sm table-custom-radius">
              <TableHeader columns={[
                { label: 'CNPJ' },
                { label: 'Nome' },
                { label: 'Cidade' },
                { label: 'UF' },
              ]} />
              <tbody>
                {!isLoading && clientesPaginados.length > 0 ? (
                  clientesPaginados.map(cliente => {
                    const isSelected =
                      selectedCliente && selectedCliente.cnpj === cliente.cnpj;
                    return (
                      <tr
                        key={cliente.cnpj}
                        onClick={() => setSelectedCliente(cliente)}
                        onDoubleClick={() => handleDoubleClick(cliente)}
                        style={{ cursor: 'pointer' }}
                        className={isSelected ? 'table-active' : ''}
                        title="Clique para selecionar, duplo clique para confirmar"
                      >
                        <td
                          className={isSelected ? 'fw-bold text-primary' : ''}
                        >
                          {cliente.cnpj}
                        </td>
                        <td
                          className={isSelected ? 'fw-bold text-primary' : ''}
                        >
                          {cliente.nome}
                        </td>
                        <td
                          className={isSelected ? 'fw-bold text-primary' : ''}
                        >
                          {cliente.logrCidade}
                        </td>
                        <td
                          className={isSelected ? 'fw-bold text-primary' : ''}
                        >
                          {cliente.logrUf}
                        </td>
                      </tr>
                    );
                  })
                ) : !isLoading ? (
                  <tr>
                    <td colSpan="4" className="text-center text-muted py-4">
                      <i className="bi bi-search display-6 d-block mb-2"></i>
                      {filtro
                        ? 'Nenhum cliente encontrado'
                        : 'Digite para buscar clientes'}
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>

          {/* Paginação */}
          {!isLoading && clientesFiltrados.length > 0 && (
            <PageFooter style={{ marginTop: '0.5rem' }}>
              <Pagination10
                totalItems={clientesFiltrados.length}
                rowsPerPage={rowsPerPage}
                startIndex={startIndex}
                onRowsPerPageChange={handleRowsPerPageChange}
                onFirstItems={handleFirstItems}
                onPreviousItems={handlePreviousItems}
                onNextItems={handleNextItems}
                onLastItems={handleLastItems}
              />
            </PageFooter>
          )}
        </Modal.Body>

        <Modal.Footer className="d-flex justify-content-end gap-2">
          <Button variant="secondary" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSelect}
            disabled={!selectedCliente}
          >
            <i className="bi bi-check-lg me-1"></i>
            Selecionar
          </Button>
        </Modal.Footer>
      </Modal>

      <LoadingModal loading={isLoading} />
    </>
  );
};

ClienteBuscaModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
};

export default ClienteBuscaModal;
